/**
 * Wege durch die Anwendung.
 *
 * Ohne Anmeldung gibt es genau eine Seite. Kein Weg führt an ihr vorbei —
 * die Prüfung sitzt hier, nicht in jeder einzelnen Seite, damit eine neue
 * Seite nicht versehentlich offen steht.
 */
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Geruest } from './bausteine/Layout';
import { Laedt } from './bausteine/Zustand';
import { Anmelden } from './seiten/Anmelden';
import { Auswertung } from './seiten/Auswertung';
import { Bank } from './seiten/Bank';
import { Konto } from './seiten/Konto';
import { Kontakte } from './seiten/Kontakte';
import { AngebotDetail } from './seiten/AngebotDetail';
import { AngebotNeu } from './seiten/AngebotNeu';
import { Angebote } from './seiten/Angebote';
import { Belege } from './seiten/Belege';
import { BelegDetail } from './seiten/BelegDetail';
import { BelegNeu } from './seiten/BelegNeu';
import { RechnungDetail } from './seiten/RechnungDetail';
import { RechnungNeu } from './seiten/RechnungNeu';
import { Rechnungen } from './seiten/Rechnungen';
import { Stammdaten } from './seiten/Stammdaten';
import { Uebersicht } from './seiten/Uebersicht';
import { useSitzung } from './sitzung/SitzungKontext';

export function App(): ReactNode {
  const { benutzer, wirdGeprueft } = useSitzung();

  // Solange /api/ich aussteht, wird nichts entschieden: Sonst blitzt bei
  // jedem Neuladen die Anmeldemaske auf, obwohl die Sitzung gilt.
  if (wirdGeprueft) return <Laedt was="Anmeldung wird geprüft" />;

  if (!benutzer) return <Anmelden />;

  return (
    <Routes>
      <Route element={<Geruest />}>
        <Route path="/" element={<Uebersicht />} />
        <Route path="/belege" element={<Belege />} />
        {/* "neu" steht vor ":id" — sonst würde es als Kennung gelesen. */}
        <Route path="/belege/neu" element={<BelegNeu />} />
        <Route path="/belege/:id" element={<BelegDetail />} />

        <Route path="/rechnungen" element={<Rechnungen />} />
        <Route path="/rechnungen/neu" element={<RechnungNeu />} />
        <Route path="/rechnungen/:id" element={<RechnungDetail />} />
        {/* Dieselbe Maske wie „neu", nur mit geladenem Entwurf. */}
        <Route path="/rechnungen/:id/aendern" element={<RechnungNeu />} />

        <Route path="/angebote" element={<Angebote />} />
        <Route path="/angebote/neu" element={<AngebotNeu />} />
        <Route path="/angebote/:id" element={<AngebotDetail />} />
        <Route path="/angebote/:id/aendern" element={<AngebotNeu />} />

        <Route path="/kontakte" element={<Kontakte />} />
        <Route path="/bank" element={<Bank />} />
        <Route path="/auswertung" element={<Auswertung />} />

        <Route path="/stammdaten" element={<Stammdaten />} />
        <Route path="/konto" element={<Konto />} />
        {/* Ein unbekannter Weg führt zur Übersicht, nicht auf eine
            Fehlerseite: In einer Anwendung mit einem Benutzer ist ein
            Tippfehler in der Adresse der einzige Weg hierher. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
